import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProgressBar from './components/ProgressBar';
import DecoDivider from './components/DecoDivider';
import useViewportInsets from './hooks/useViewportInsets';
import { logUxEvent } from './telemetry/uxLogger';
import {
  nextSignupRoute,
  getSignupVariant,
} from './utils/signupOrderAb';
import { usesGuidedCopy, signupUxProgress } from './utils/signupUxAb';
import { signupCta, signupStepTitle } from './utils/signupUxCopy';
import SignupStepHeading from './components/SignupStepHeading';

const STORAGE_KEY = 'instantCouponAb';

export function readInstantCouponAb() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.couponCode) return null;
    if (parsed.couponExpiresMs && Number(parsed.couponExpiresMs) < Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function persistInstantCouponAb(payload) {
  try {
    const json = JSON.stringify(payload);
    sessionStorage.setItem(STORAGE_KEY, json);
    localStorage.setItem(STORAGE_KEY, json);
  } catch { /* ignore */ }
}

const InstantCouponOffer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useViewportInsets();
  const guided = usesGuidedCopy();

  const offer = useMemo(() => {
    const fromState = location.state && location.state.offer;
    if (fromState && fromState.couponCode) return fromState;
    return readInstantCouponAb();
  }, [location.state]);

  useEffect(() => {
    if (!offer || !offer.couponCode) {
      const variant = getSignupVariant();
      const dest = nextSignupRoute('cpf', variant) || '/telefone';
      navigate(dest, { replace: true });
      return;
    }
    logUxEvent('instant_coupon_offer_shown', {
      priceCents: offer.priceCents || null,
      codePrefix: String(offer.couponCode || '').slice(0, 6),
    });
  }, [offer, navigate]);

  const priceLabel = useMemo(() => {
    const cents = Number(offer?.priceCents || 0);
    if (!cents) return '';
    return (cents / 100).toFixed(2).replace('.', ',');
  }, [offer]);

  const handleContinue = () => {
    logUxEvent('instant_coupon_offer_continue', {
      priceCents: offer?.priceCents || null,
    });
    const variant = getSignupVariant();
    const dest = nextSignupRoute('cpf', variant) || '/telefone';
    navigate(dest);
  };

  if (!offer || !offer.couponCode) return null;

  return (
    <>
      <style>{`
        :root {
          --primary: #D32F2F;
          --bg-page: #F9FAFB;
          --surface: #FFFFFF;
          --text-main: #1E1E1E;
          --text-dim: #6B6B6B;
          --ok: #15803d;
          --ok-bg: #f0fdf4;
          --radius-xl: 1.5rem;
          --radius-full: 999px;
        }
        body {
          margin: 0;
          background: var(--bg-page);
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Inter', 'Roboto', sans-serif;
          color: var(--text-main);
        }
        .ico-shell {
          min-height: 100vh;
          min-height: 100dvh;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          background: var(--bg-page);
        }
        .ico-inner {
          flex: 1;
          max-width: 480px;
          width: 100%;
          margin: 0 auto;
          padding: 4.5rem 1rem max(2rem, calc(1rem + var(--vvb, 0px) + env(safe-area-inset-bottom, 0px)));
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 1.25rem;
          box-sizing: border-box;
        }
        .ico-header { text-align: center; max-width: 22rem; }
        .ico-title {
          font-size: 1.25rem;
          line-height: 1.35;
          font-weight: 600;
          margin: 0 0 .75rem;
        }
        .ico-card {
          width: 100%;
          max-width: 400px;
          background: var(--surface);
          border-radius: var(--radius-xl);
          border: 1px solid rgba(0,0,0,0.06);
          box-shadow: 0 6px 16px rgba(0,0,0,0.06);
          padding: 1.5rem 1.25rem 1.25rem;
          text-align: center;
          box-sizing: border-box;
        }
        .ico-badge {
          display: inline-block;
          background: var(--ok-bg);
          color: var(--ok);
          font-size: .75rem;
          font-weight: 600;
          padding: .35rem .7rem;
          border-radius: var(--radius-full);
          margin-bottom: .85rem;
        }
        .ico-price {
          font-size: 2rem;
          font-weight: 700;
          color: var(--ok);
          margin: 0 0 .35rem;
          letter-spacing: -0.02em;
        }
        .ico-strike {
          font-size: .9rem;
          color: var(--text-dim);
          text-decoration: line-through;
          margin: 0 0 1rem;
        }
        .ico-copy {
          font-size: .9rem;
          line-height: 1.45;
          color: var(--text-dim);
          margin: 0 0 1.25rem;
        }
        .ico-code {
          font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
          font-size: .85rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          background: #f3f4f6;
          border-radius: .75rem;
          padding: .65rem .85rem;
          margin: 0 0 1.25rem;
          color: var(--text-main);
        }
        .ico-btn {
          background: var(--primary);
          color: #FFF;
          border: none;
          border-radius: var(--radius-full);
          font-size: 1.05rem;
          font-weight: 600;
          padding: 1rem 1.5rem;
          min-width: 200px;
          min-height: 48px;
          cursor: pointer;
        }
        .ico-btn:hover { background: #B71C1C; }
        .ico-note {
          margin-top: .85rem;
          font-size: .75rem;
          color: var(--text-dim);
          line-height: 1.4;
        }
      `}</style>

      <div className="ico-shell">
        <div className="ico-inner">
          <ProgressBar {...signupUxProgress('/cupom-primeira', getSignupVariant())} />
          <div className="ico-header">
            <h1 className="ico-title deco-headline">Cupom liberado para você</h1>
            <DecoDivider variant="chevron" />
          </div>

          <div className="ico-card">
            {guided ? <SignupStepHeading title={signupStepTitle('/cupom-primeira')} /> : null}
            <div className="ico-badge">Primeira consulta</div>
            <p className="ico-price">R$ {priceLabel}</p>
            <p className="ico-strike">R$ 79,90</p>
            <p className="ico-copy">
              Seu desconto já fica aplicado no pagamento. Válido por 24 horas.
            </p>
            <div className="ico-code" aria-label="Código do cupom">{offer.couponCode}</div>
            <button type="button" className="ico-btn" onClick={handleContinue}>
              {signupCta('cupom', { guided })}
            </button>
            <p className="ico-note">
              Sem cadastro extra — é só seguir o fluxo e o valor já sai com desconto.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstantCouponOffer;
